import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Building2, ChevronLeft, ChevronRight, Newspaper, ExternalLink,
  Star, Shield, TrendingUp, CheckCircle2, Download, PlayCircle,
  Send, Mail, FileText,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { useState } from "react";
import { getArticleUrl } from "@/lib/article-url";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const PAGE_SIZE = 12;

interface PublicCompany {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  website: string | null;
  description: string | null;
  industry: string | null;
  headquarters: string | null;
  founded: string | null;
  size: string | null;
  heroBanner?: string | null;
  tagline?: string | null;
  showActPanel?: boolean | null;
}

interface PublicArticleSnippet {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  publishedAt: string | null;
  createdAt: string;
}

interface MembershipTier {
  name: string;
  slug: string;
}

interface ActAggregates {
  avgAwareness: number | null;
  avgConfidence: number | null;
  avgTrust: number | null;
  scoredCount: number;
}

// From GET /api/public/companies/:slug — profile + member features
interface CompanyProfileResponse {
  company: PublicCompany;
  featuredArticles: PublicArticleSnippet[];
  membershipTier: MembershipTier | null;
  membershipStatus: string | null;
  actAggregates: ActAggregates | null;
  socialWallSlug: string | null;
  problemsSolvedCount: number;
  total: number;
}

// From GET /api/companies/:slug/articles — paginated article list
interface CompanyArticlesResponse {
  articles: PublicArticleSnippet[];
  total: number;
  page: number;
  totalPages: number;
}

// ─── Membership badge ──────────────────────────────────────────────────────────

function MembershipBadge({ tier }: { tier: MembershipTier }) {
  if (tier.slug === "founding-partner") {
    return (
      <Badge className="bg-amber-500 hover:bg-amber-500 text-white flex items-center gap-1 px-3 py-1" data-testid="badge-founding-partner">
        <Star className="h-3 w-3 fill-white" />
        Founding Partner
      </Badge>
    );
  }
  if (tier.slug === "premium") {
    return (
      <Badge className="bg-primary text-primary-foreground flex items-center gap-1 px-3 py-1" data-testid="badge-premium-member">
        <Shield className="h-3 w-3" />
        Premium Member
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="flex items-center gap-1" data-testid="badge-standard-member">
      Member
    </Badge>
  );
}

// ─── ACT score panel ───────────────────────────────────────────────────────────

function ActScoreBar({ label, value, color }: { label: string; value: number | null; color: string }) {
  if (value == null) return null;
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="space-y-1" data-testid={`act-score-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground font-mono">{value}/100</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ActPanel({ aggregates }: { aggregates: ActAggregates }) {
  return (
    <div className="rounded-xl border bg-card p-6 space-y-4" data-testid="act-panel">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-lg">Editorial Trust Scores</h3>
        <span className="text-xs text-muted-foreground ml-auto">
          Based on {aggregates.scoredCount} article{aggregates.scoredCount !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="space-y-3">
        <ActScoreBar label="Awareness" value={aggregates.avgAwareness} color="bg-blue-500" />
        <ActScoreBar label="Confidence" value={aggregates.avgConfidence} color="bg-green-500" />
        <ActScoreBar label="Trust" value={aggregates.avgTrust} color="bg-purple-500" />
      </div>
      <p className="text-xs text-muted-foreground pt-1">
        ACT scores reflect the quality and credibility of this company's published content, as assessed by the Future Manufacturing editorial team.
      </p>
    </div>
  );
}

// ─── Thought Leadership articles (shared by Standard and Premium/Founding) ─────

function FeaturedArticleCard({ article, index }: { article: PublicArticleSnippet; index: number }) {
  const isVideo = article.featuredImage?.includes("youtube") || article.featuredImage?.includes("video");
  return (
    <Link href={getArticleUrl(article)} data-testid={`link-featured-article-${article.id}`}>
      <Card className="group hover-elevate h-full overflow-visible flex flex-col cursor-pointer border-primary/20 bg-primary/5">
        <div className="aspect-[16/10] w-full overflow-hidden rounded-t-md bg-muted relative">
          {article.featuredImage ? (
            <img
              src={article.featuredImage}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
              <Newspaper className="h-8 w-8" />
            </div>
          )}
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black/50 rounded-full p-2">
                <PlayCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          )}
          <div className="absolute top-2 left-2">
            <Badge className="bg-amber-500/90 hover:bg-amber-500/90 text-white text-[10px] px-1.5 py-0.5 flex items-center gap-1">
              <Star className="h-2.5 w-2.5 fill-white" />
              {index === 0 ? "Top Pick" : "Featured"}
            </Badge>
          </div>
        </div>
        <CardContent className="pt-4 pb-4 flex-1 flex flex-col">
          <h3 className="font-semibold text-base line-clamp-2 group-hover:text-primary transition-colors mb-2" data-testid={`text-featured-title-${article.id}`}>
            {article.title}
          </h3>
          {article.excerpt && (
            <p className="text-sm text-muted-foreground line-clamp-2 flex-1">{article.excerpt}</p>
          )}
          {article.publishedAt && (
            <p className="text-xs text-muted-foreground mt-3">
              {new Date(article.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function FeaturedArticlesSection({ articles, isPremium }: { articles: PublicArticleSnippet[]; isPremium: boolean }) {
  const [carouselIdx, setCarouselIdx] = useState(0);
  if (articles.length === 0) return null;

  const prev = () => setCarouselIdx(i => (i - 1 + articles.length) % articles.length);
  const next = () => setCarouselIdx(i => (i + 1) % articles.length);

  return (
    <div className="mb-8" data-testid="featured-articles-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-bold">Thought Leadership</h2>
        </div>
        {isPremium && articles.length > 1 && (
          <div className="flex items-center gap-1 md:hidden">
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={prev} data-testid="button-carousel-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground px-1">{carouselIdx + 1}/{articles.length}</span>
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={next} data-testid="button-carousel-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {isPremium ? (
        <>
          <div className="md:hidden">
            <FeaturedArticleCard article={articles[carouselIdx]} index={carouselIdx} />
          </div>
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((a, idx) => <FeaturedArticleCard key={a.id} article={a} index={idx} />)}
          </div>
        </>
      ) : (
        // Standard members: condensed list without carousel
        <div className="space-y-3">
          {articles.slice(0, 3).map((a, idx) => (
            <Link key={a.id} href={getArticleUrl(a)} data-testid={`link-featured-article-${a.id}`}>
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/40 transition-colors cursor-pointer group">
                <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md bg-amber-500/10 text-amber-600 text-xs font-bold">
                  {idx + 1}
                </div>
                {a.featuredImage && (
                  <div className="w-16 h-12 rounded-md overflow-hidden bg-muted flex-shrink-0">
                    <img src={a.featuredImage} alt={a.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors" data-testid={`text-featured-title-${a.id}`}>
                    {a.title}
                  </p>
                  {a.publishedAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(a.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Social wall embed (Standard+ feature) ────────────────────────────────────

function SocialWallEmbed({ slug }: { slug: string }) {
  return (
    <div className="mb-8" data-testid="social-wall-embed">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-semibold text-base">Latest Updates</h3>
        <Link
          href={`/social/${slug}`}
          className="text-xs text-muted-foreground hover:text-primary transition-colors ml-auto flex items-center gap-1"
          data-testid="link-social-wall-full"
        >
          View all
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      <div className="rounded-xl border bg-card overflow-hidden" style={{ minHeight: "200px" }}>
        <iframe
          src={`/social/${slug}`}
          title="Company social feed"
          className="w-full"
          style={{ height: "320px", border: "none" }}
          data-testid="iframe-social-wall"
          loading="lazy"
        />
      </div>
    </div>
  );
}

// ─── Enquiry form (Standard+ feature) ─────────────────────────────────────────

interface EnquiryFormState {
  name: string;
  email: string;
  company: string;
  message: string;
}

function EnquirySection({ targetCompanyName, targetCompanyId }: { targetCompanyName: string; targetCompanyId: string }) {
  const { toast } = useToast();
  const [form, setForm] = useState<EnquiryFormState>({ name: "", email: "", company: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: EnquiryFormState) =>
      apiRequest("POST", `/api/companies/${targetCompanyId}/enquiry`, data),
    onSuccess: () => {
      setSubmitted(true);
      toast({ title: "Enquiry sent", description: `Your message has been sent to ${targetCompanyName}.` });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send", description: error.message, variant: "destructive" });
    },
  });

  return (
    <div className="mb-8 rounded-xl border bg-card p-6" data-testid="enquiry-section">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-lg">Get in Touch</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Send an enquiry to {targetCompanyName} and their team will respond.
      </p>
      {submitted ? (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-green-500/20 bg-green-500/5 text-green-700 dark:text-green-400" data-testid="enquiry-submitted">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium text-sm">Enquiry submitted!</p>
            <p className="text-xs mt-0.5 opacity-80">A team member will be in touch shortly.</p>
          </div>
        </div>
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
            mutation.mutate(form);
          }}
          className="space-y-4"
          data-testid="enquiry-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-name">Your Name *</Label>
              <Input
                id="enquiry-name"
                placeholder="Jane Smith"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
                data-testid="input-enquiry-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-email">Email Address *</Label>
              <Input
                id="enquiry-email"
                type="email"
                placeholder="jane@example.com"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
                data-testid="input-enquiry-email"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="enquiry-company">Your Company</Label>
            <Input
              id="enquiry-company"
              placeholder="Acme Corp"
              value={form.company}
              onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
              data-testid="input-enquiry-company"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="enquiry-message">Message *</Label>
            <Textarea
              id="enquiry-message"
              placeholder={`How can ${targetCompanyName} help you?`}
              rows={4}
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              required
              data-testid="input-enquiry-message"
            />
          </div>
          <Button type="submit" disabled={mutation.isPending} data-testid="button-send-enquiry" className="w-full sm:w-auto">
            <Send className="h-4 w-4 mr-2" />
            {mutation.isPending ? "Sending…" : "Send Enquiry"}
          </Button>
        </form>
      )}
    </div>
  );
}

// ─── Media Pack placeholder (Premium/Founding exclusive) ──────────────────────

function MediaPackSection({ companyName }: { companyName: string }) {
  return (
    <div className="mb-8 rounded-xl border bg-muted/30 p-6" data-testid="media-pack-section">
      <div className="flex items-start gap-4">
        <div className="rounded-lg bg-primary/10 p-3 shrink-0">
          <Download className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-base mb-1">Media Pack</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Download the official {companyName} media pack, including brand assets, case studies, and editorial guidelines for press and partners.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {["Brand Guidelines", "Press Kit", "Case Studies"].map(label => (
              <div key={label} className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg px-3 py-2 bg-background">
                <FileText className="h-4 w-4" />
                <span>{label}</span>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" data-testid="button-request-media-pack">
            <Mail className="h-4 w-4 mr-2" />
            Request Media Pack
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Non-member CTA ───────────────────────────────────────────────────────────

function NonMemberCta() {
  return (
    <div className="mb-8 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-6" data-testid="non-member-cta">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <h3 className="font-semibold text-base mb-1">Is this your company?</h3>
          <p className="text-sm text-muted-foreground">
            Unlock a company profile with editorial trust scores, thought leadership showcases, a direct enquiry form, and more.
          </p>
        </div>
        <Button asChild size="sm" className="shrink-0" data-testid="button-join-as-member">
          <Link href="/membership">Join as Member</Link>
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function CompanyHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  // Profile query: company metadata + member-tier features
  const { data: profile, isLoading: profileLoading } = useQuery<CompanyProfileResponse>({
    queryKey: [`/api/public/companies/${slug}`],
    enabled: !!slug,
  });

  // Articles query: paginated list of linked published articles
  const { data: articlesData } = useQuery<CompanyArticlesResponse>({
    queryKey: [`/api/companies/${slug}/articles?page=${page}&limit=${PAGE_SIZE}`],
    enabled: !!slug,
  });

  // Current user — used to show admin-only link performance section
  const { data: currentUser } = useQuery<{ id: string; role: string } | null>({
    queryKey: ['/api/auth/user'],
  });
  const isAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  // Tracked link stats for this company — admin only
  const { data: linkStats } = useQuery<{ id: string; code: string; label: string; destinationUrl: string; clickCount: number }[]>({
    queryKey: [`/api/admin/companies/${profile?.company?.id}/tracked-link-stats`],
    enabled: isAdmin && !!profile?.company?.id,
  });

  const isLoading = profileLoading;
  const company = profile?.company;
  const articles = articlesData?.articles ?? [];
  const featuredArticles = profile?.featuredArticles ?? [];
  const membershipTier = profile?.membershipTier ?? null;
  const actAggregates = profile?.actAggregates ?? null;
  const socialWallSlug = profile?.socialWallSlug ?? null;
  const problemsSolvedCount = profile?.problemsSolvedCount ?? 0;
  const totalPages = articlesData?.totalPages ?? 1;
  const articleTotal = profile?.total ?? articlesData?.total ?? 0;

  const isPremiumOrAbove =
    membershipTier?.slug === "premium" || membershipTier?.slug === "founding-partner";
  const isMember = !!membershipTier;

  const pageTitle = company ? `${company.name} — Future Manufacturing` : "Company — Future Manufacturing";
  const pageDescription = company?.description
    ? company.description.slice(0, 160)
    : `News, articles and insights for ${company?.name ?? "this company"} on Future Manufacturing.`;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="max-w-7xl mx-auto px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Companies" }, { label: "Loading…" }]} />
        </div>
        <div className="flex-1 max-w-7xl mx-auto px-4 py-10 w-full">
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-5 w-96 mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-lg" />)}
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Company Not Found</h1>
          <p className="text-muted-foreground mb-6">This company page doesn't exist.</p>
          <Button asChild><Link href="/">Go Home</Link></Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {company.logo && <meta property="og:image" content={company.logo} />}
        <meta property="og:type" content="profile" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: company.name,
          ...(company.website ? { url: company.website } : {}),
          ...(company.logo ? { logo: company.logo } : {}),
          ...(company.description ? { description: company.description } : {}),
        })}</script>
      </Helmet>

      <SiteHeader />

      {/* Hero Banner — Premium/Founding only */}
      {isPremiumOrAbove && company.heroBanner && (
        <div className="w-full h-56 md:h-72 lg:h-80 bg-muted relative overflow-hidden" data-testid="company-hero-banner">
          <img src={company.heroBanner} alt={`${company.name} banner`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/10 to-transparent" />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 pt-4 w-full">
        <PageBreadcrumb items={[{ label: "Companies" }, { label: company.name }]} />
      </div>

      <div className="flex-1 max-w-7xl mx-auto px-4 pb-8 lg:pb-12 w-full">
        {/* Company Header */}
        <div className={`flex items-start gap-6 pb-8 border-b ${isPremiumOrAbove && company.heroBanner ? "mt-4 mb-8" : "mb-10"}`}>
          <div className="w-20 h-20 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm">
            {company.logo
              ? <img src={company.logo} alt={company.name} className="w-full h-full object-contain p-2" />
              : <Building2 className="h-10 w-10 text-muted-foreground" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <h1 className="text-3xl font-bold" data-testid="text-company-name">{company.name}</h1>
              {membershipTier && <MembershipBadge tier={membershipTier} />}
              {company.website && (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
                  data-testid="link-company-website"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Website
                </a>
              )}
            </div>

            {/* Tagline — Premium/Founding only */}
            {isPremiumOrAbove && company.tagline && (
              <p className="text-base font-medium text-primary mt-1" data-testid="text-company-tagline">
                {company.tagline}
              </p>
            )}

            {company.description && (
              <p className="text-muted-foreground text-base leading-relaxed mt-2" data-testid="text-company-description">
                {company.description}
              </p>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-muted-foreground">
              {company.industry && <span data-testid="text-company-industry">{company.industry}</span>}
              {company.headquarters && <span data-testid="text-company-headquarters">{company.headquarters}</span>}
              {company.founded && <span>Founded {company.founded}</span>}
              {company.size && <span>{company.size}</span>}
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
              <span data-testid="text-article-count">
                {articleTotal} article{articleTotal !== 1 ? "s" : ""}
              </span>
              {problemsSolvedCount > 0 && (
                <span className="flex items-center gap-1" data-testid="text-problems-solved">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  {problemsSolvedCount} problem{problemsSolvedCount !== 1 ? "s" : ""} solved
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Non-member CTA */}
        {!isMember && <NonMemberCta />}

        {/*
          Member content sections — tiered:
          Standard gets: ACT panel, featured highlights (condensed), enquiry form, social wall embed
          Premium/Founding inherits all Standard + adds: full carousel, media pack (hero/tagline shown in header)
        */}

        {/* ACT panel — shown for all members whenever scored data is available.
            For Standard: always shown when there are scored articles (no admin toggle required).
            For Premium/Founding: shown when showActPanel is enabled and data exists. */}
        {isMember && actAggregates && (isPremiumOrAbove ? !!company.showActPanel : true) && (
          <div className="mb-8">
            <ActPanel aggregates={actAggregates} />
          </div>
        )}

        {/* Featured / Thought Leadership — all members */}
        {isMember && featuredArticles.length > 0 && (
          <FeaturedArticlesSection articles={featuredArticles} isPremium={isPremiumOrAbove} />
        )}

        {/* Social wall embed — all members */}
        {isMember && socialWallSlug && <SocialWallEmbed slug={socialWallSlug} />}

        {/* Enquiry form — all members */}
        {isMember && <EnquirySection targetCompanyName={company.name} targetCompanyId={company.id} />}

        {/* Media Pack — Premium/Founding exclusive */}
        {isPremiumOrAbove && <MediaPackSection companyName={company.name} />}

        {/* Articles grid */}
        {articles.length === 0 && featuredArticles.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Newspaper className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg">No published articles yet for this company.</p>
          </div>
        ) : articles.length > 0 ? (
          <>
            {featuredArticles.length > 0 && (
              <h2 className="text-xl font-bold mb-4" data-testid="text-latest-articles-heading">Latest Articles</h2>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {articles.map(article => (
                <Link key={article.id} href={getArticleUrl(article)} data-testid={`link-article-${article.id}`}>
                  <Card className="group hover-elevate h-full overflow-visible flex flex-col cursor-pointer">
                    {article.featuredImage && (
                      <div className="aspect-[16/10] w-full overflow-hidden rounded-t-md bg-muted">
                        <img
                          src={article.featuredImage}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <CardContent className="pt-4 pb-4 flex-1 flex flex-col">
                      <h2 className="font-semibold text-base line-clamp-2 group-hover:text-primary transition-colors mb-2" data-testid={`text-article-title-${article.id}`}>
                        {article.title}
                      </h2>
                      {article.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-2 flex-1">{article.excerpt}</p>
                      )}
                      {article.publishedAt && (
                        <p className="text-xs text-muted-foreground mt-3">
                          {new Date(article.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2" data-testid="pagination-controls">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="button-prev-page">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="button-next-page">
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Admin-only: Link Performance Section */}
      {isAdmin && linkStats && linkStats.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 pb-8 w-full">
          <div className="border rounded-lg bg-muted/30 p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800">
                Admin only
              </span>
              <h3 className="font-semibold text-sm">Link Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Label</th>
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground hidden md:table-cell">Destination</th>
                    <th className="text-right py-2 font-medium text-muted-foreground w-20">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {linkStats.map(link => (
                    <tr key={link.id} className="border-b last:border-0" data-testid={`row-company-link-${link.id}`}>
                      <td className="py-2 pr-4">
                        <span className="font-medium">{link.label}</span>
                        <code className="ml-1.5 text-xs text-muted-foreground font-mono">/go/{link.code}</code>
                      </td>
                      <td className="py-2 pr-4 hidden md:table-cell">
                        <a
                          href={link.destinationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:text-primary truncate block max-w-xs"
                        >
                          {link.destinationUrl}
                        </a>
                      </td>
                      <td className="py-2 text-right tabular-nums font-medium" data-testid={`text-link-clicks-${link.id}`}>
                        {link.clickCount > 0 ? link.clickCount.toLocaleString() : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}
